(() => {
  if (customElements.get('cl-ai-crown-chat')) return;

  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const MAX_TURNS = 8;
  const RECOVERY_VERSION = 2;
  const RECOVERY_TTL = 60 * 60 * 1000;
  const RECOVERY_MAX_LENGTH = 12_000;

  class CrownChat extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') { this.setupChallenge(); this.scheduleExpiry(); this.syncControls(); return; }
      this.dataset.ready = 'true';
      this.locale = this.dataset.locale === 'en' ? 'en' : 'it';
      this.messages = this.querySelector('[data-ai-chat-messages]');
      this.form = this.querySelector('[data-ai-chat-form]');
      this.input = this.querySelector('[data-ai-chat-input]');
      this.delivery = this.querySelector('[data-ai-chat-delivery]');
      this.challenge = this.querySelector('[data-ai-chat-challenge]');
      this.notice = this.querySelector('[data-ai-chat-notice]');
      this.submitButton = this.querySelector('[data-ai-chat-submit]');
      this.turnsCountNode = this.querySelector('[data-ai-chat-turns-count]');
      this.qaProbeButton = this.querySelector('[data-ai-chat-qa-probe]');
      this.qaCodeField = this.querySelector('[data-ai-chat-qa-code]');
      this.qaCopyButton = this.querySelector('[data-ai-chat-qa-copy]');
      this.qaCodeStatus = this.querySelector('[data-ai-chat-qa-code-status]');
      this.qaCode = '';
      this.qaMode = this.dataset.qaMode === 'true';
      this.qaAutomatic = this.qaMode && this.dataset.serviceReady === 'true';
      this.qaAutoProbeStarted = false;
      this.qaSessionReady = false;
      this.sessionKey = 'corone-ai-preview-session-v2';
      this.chatAttemptKey = 'corone-ai-consultation-attempt-v2';
      this.previewAttemptKey = 'corone-ai-preview-attempt-v2';
      this.recoveryKey = 'corone-ai-completed-reply-v1';
      this.endpoint = this.safeEndpoint(this.dataset.endpoint);
      this.storageAvailable = true;
      this.adoptQaInvitation();
      this.sessionId = this.storageAvailable ? this.readOrCreateSessionId() : '';
      this.chatAttempt = this.readAttempt(this.chatAttemptKey, 'CONSULTATION');
      this.previewAttempt = this.readAttempt(this.previewAttemptKey, 'PREVIEW');
      this.turnCount = this.chatAttempt?.turnCount || 0;
      this.previewLocked = Boolean(this.previewAttempt);
      this.operationLocked = !this.storageAvailable
        || Boolean(this.chatAttempt && !['SUCCEEDED', 'NOT_ADMITTED'].includes(this.chatAttempt.status))
        || Boolean(this.previewAttempt && this.previewAttempt.status !== 'SUCCEEDED');
      this.currentProposal = null;
      this.turnstileToken = null;
      this.form?.addEventListener('submit', (event) => this.handleIdea(event));
      this.qaProbeButton?.addEventListener('click', () => this.checkQaSession());
      this.qaCopyButton?.addEventListener('click', () => this.copyQaCode());
      const invalidateEditableProposal = () => {
        if (this.turnCount < MAX_TURNS && !this.previewLocked) this.invalidateProposal();
      };
      this.input?.addEventListener('input', invalidateEditableProposal);
      this.delivery?.addEventListener('change', invalidateEditableProposal);
      this.messages?.addEventListener('click', (event) => {
        const copyTrigger = event.target.closest('[data-ai-chat-copy]');
        if (copyTrigger && this.messages.contains(copyTrigger)) { this.copyCompletedText(copyTrigger); return; }
        const trigger = event.target.closest('[data-ai-chat-generate]');
        if (trigger && this.messages.contains(trigger)) this.requestPreview(trigger);
      });
      this.restoreCompletedReply();
      if (!this.serviceAvailable()) this.showNotice(this.dataset.disabledLabel);
      else if (!this.storageAvailable) this.showNotice(this.dataset.storageErrorLabel);
      else if (this.operationLocked) this.showNotice(this.dataset.unknownLabel);
      else if (this.previewLocked) this.showNotice(this.dataset.completedLabel, 'progress');
      else if (this.turnCount >= MAX_TURNS) this.showNotice(this.dataset.sessionLimitLabel);
      else {
        this.setupChallenge();
        if (this.qaMode) this.showNotice(this.dataset.qaRequiredLabel, 'progress');
      }
      this.syncControls();
      this.qaCodePreparation = this.prepareQaCode();
      if (this.qaAutomatic && this.serviceAvailable()) this.checkQaSession();
    }

    disconnectedCallback() {
      clearTimeout(this.expiryTimer);
      window.removeEventListener('load', this.challengeLoad);
      if (this.widgetId !== undefined && window.turnstile) window.turnstile.remove(this.widgetId);
      this.widgetId = undefined;
      this.turnstileToken = null;
    }

    safeEndpoint(value) {
      try {
        const url = new URL(value);
        if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash) return '';
        return `${url.origin}${url.pathname.replace(/\/$/, '')}`;
      } catch { return ''; }
    }

    serviceAvailable() {
      return this.dataset.serviceReady === 'true' && Boolean(this.endpoint && this.dataset.turnstileSitekey);
    }

    qaAccessReady() {
      return !this.qaMode || this.qaSessionReady === true;
    }

    qaCodeEligible() {
      if (!this.qaMode || !this.storageAvailable || this.operationLocked || this.chatAttempt || this.previewAttempt
        || !UUID_V4.test(this.sessionId)) return false;
      try {
        return sessionStorage.getItem(this.sessionKey) === this.sessionId
          && sessionStorage.getItem(this.chatAttemptKey) === null
          && sessionStorage.getItem(this.previewAttemptKey) === null;
      } catch { return false; }
    }

    setQaCodeStatus(text, state) {
      if (!this.qaCodeStatus) return;
      this.qaCodeStatus.textContent = text || '';
      this.qaCodeStatus.dataset.state = state;
    }

    syncQaCodeControls() {
      const eligible = this.qaCodeEligible();
      if (!eligible) {
        const hadCode = Boolean(this.qaCode || this.qaCodeField?.value);
        this.qaCode = '';
        if (this.qaCodeField) this.qaCodeField.value = '';
        if (hadCode) this.setQaCodeStatus(this.dataset.qaCodeErrorLabel, 'error');
      }
      if (this.qaCodeField) {
        this.qaCodeField.readOnly = true;
        this.qaCodeField.setAttribute('aria-busy', String(Boolean(this.qaCodeBusy)));
      }
      if (this.qaCopyButton) {
        this.qaCopyButton.disabled = !eligible || Boolean(this.qaCodeBusy || this.qaCopyBusy)
          || !/^[0-9a-f]{64}$/.test(this.qaCode);
        this.qaCopyButton.setAttribute('aria-disabled', String(this.qaCopyButton.disabled));
        this.qaCopyButton.setAttribute('aria-busy', String(Boolean(this.qaCopyBusy)));
      }
    }

    async prepareQaCode() {
      if (!this.qaMode || !this.qaCodeField || this.qaCodeBusy) return;
      if (!this.qaCodeEligible()) {
        this.setQaCodeStatus(this.dataset.qaCodeErrorLabel, 'error');
        this.syncQaCodeControls();
        return;
      }
      const sessionId = this.sessionId;
      this.qaCodeBusy = true;
      this.syncQaCodeControls();
      try {
        if (typeof crypto === 'undefined' || typeof crypto.subtle?.digest !== 'function'
          || typeof TextEncoder !== 'function') throw new Error('qa_hash_unavailable');
        const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sessionId)));
        if (bytes.length !== 32 || !this.qaCodeEligible() || this.sessionId !== sessionId) throw new Error('qa_hash_unavailable');
        this.qaCode = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
        this.qaCodeField.value = this.qaCode;
        this.setQaCodeStatus('', 'ready');
      } catch {
        this.qaCode = '';
        this.qaCodeField.value = '';
        this.setQaCodeStatus(this.dataset.qaCodeErrorLabel, 'error');
      } finally {
        this.qaCodeBusy = false;
        this.syncQaCodeControls();
      }
    }

    async copyQaCode() {
      if (!this.qaMode || this.qaCopyBusy || this.qaCodeBusy) return;
      if (!this.qaCodeEligible() || !/^[0-9a-f]{64}$/.test(this.qaCode) || this.qaCodeField?.value !== this.qaCode) {
        this.qaCode = '';
        if (this.qaCodeField) this.qaCodeField.value = '';
        this.setQaCodeStatus(this.dataset.qaCodeErrorLabel, 'error');
        this.syncQaCodeControls();
        return;
      }
      const code = this.qaCode;
      this.qaCopyBusy = true;
      this.syncQaCodeControls();
      try {
        if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') throw new Error('clipboard_unavailable');
        await navigator.clipboard.writeText(code);
        if (!this.qaCodeEligible() || this.qaCode !== code) throw new Error('qa_code_unavailable');
        this.setQaCodeStatus(this.dataset.qaCopiedLabel, 'success');
      } catch {
        if (this.qaCodeEligible()) {
          this.qaCodeField?.focus();
          this.qaCodeField?.select();
        }
        this.setQaCodeStatus(this.dataset.qaCodeErrorLabel, 'error');
      } finally {
        this.qaCopyBusy = false;
        this.syncQaCodeControls();
      }
    }

    async checkQaSession() {
      if (!this.qaMode || this.qaProbeBusy || this.chatBusy || this.generationBusy || this.operationLocked
        || !this.storageAvailable || !this.endpoint || !UUID.test(this.sessionId)) return;
      if (this.qaAutomatic) {
        if (this.qaAutoProbeStarted) return;
        this.qaAutoProbeStarted = true;
      }
      this.qaSessionReady = false;
      this.qaProbeBusy = true;
      this.showNotice(this.dataset.qaCheckingLabel, 'progress');
      this.syncControls();
      try {
        const response = await fetch(`${this.endpoint}/operator/qa-session-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId }),
          signal: AbortSignal.timeout(15_000),
          credentials: 'omit',
          redirect: 'error',
        });
        const data = await this.readJson(response, 8 * 1024);
        if (!response.ok || !data || typeof data !== 'object' || Array.isArray(data)
          || Object.keys(data).length !== 2 || data.ok !== true || typeof data.qaSessionReady !== 'boolean') {
          throw new Error('invalid_qa_status');
        }
        this.qaSessionReady = data.qaSessionReady;
        this.showNotice(this.qaSessionReady ? this.dataset.qaReadyLabel
          : this.qaAutomatic ? this.dataset.chatErrorLabel : this.dataset.qaDeniedLabel, 'progress');
      } catch {
        this.showNotice(this.qaAutomatic ? this.dataset.chatErrorLabel : this.dataset.qaErrorLabel);
      } finally {
        this.qaProbeBusy = false;
        this.syncControls();
      }
    }

    async handleIdea(event) {
      event.preventDefault();
      if (this.chatBusy || this.generationBusy || this.qaProbeBusy || this.operationLocked || this.previewLocked || this.turnCount >= MAX_TURNS) return;
      if (!this.qaAccessReady()) { this.showNotice(this.dataset.qaRequiredLabel, 'progress'); return; }
      const idea = this.input?.value.trim();
      const deliveryMode = this.delivery?.value;
      if (!idea || idea.length > 600 || !['UNKNOWN', 'LOCAL', 'NATIONAL'].includes(deliveryMode)) return;
      if (!this.serviceAvailable()) { this.showNotice(this.dataset.disabledLabel); return; }
      if (!this.turnstileToken) { this.showNotice(this.dataset.challengeLabel); return; }
      const attempt = this.reserveAttempt('CONSULTATION');
      if (!attempt) return;
      const token = this.consumeToken();
      this.chatBusy = true;
      this.invalidateProposal();
      this.appendMessage('visitor', idea);
      this.input.value = '';
      this.showNotice(this.dataset.thinkingLabel, 'progress');
      this.syncControls();
      try {
        const response = await fetch(`${this.endpoint}/v2/consultations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId, requestId: attempt.requestId, idea, deliveryMode, turnstileToken: token, locale: this.locale }),
          signal: AbortSignal.timeout(75_000),
          credentials: 'omit',
          redirect: 'error',
        });
        const data = await this.readJson(response, 32 * 1024);
        if (!response.ok) {
          if (response.status === 409 && this.releaseUnadmittedConsultation(data, attempt)) {
            this.input.value = idea;
            this.showNotice(this.dataset.busyLabel, 'progress');
            return;
          }
          this.finishAttempt('CONSULTATION', 'BLOCKED');
          this.showNotice(this.dataset.lockedErrorLabel);
          return;
        }
        if (!this.validConsultation(data)) throw new Error('invalid_consultation');
        if (!this.finishAttempt('CONSULTATION', 'SUCCEEDED')) return;
        this.notice.hidden = true;
        this.currentProposal = data.proposal ? { ...data.proposal } : null;
        this.appendMessage('assistant', data.reply, this.currentProposal);
        this.cacheCompletedReply(data, attempt);
        this.scheduleExpiry();
        if (this.turnCount >= MAX_TURNS) this.showNotice(this.dataset.sessionLimitLabel);
      } catch {
        this.finishAttempt('CONSULTATION', 'UNKNOWN');
        this.showNotice(this.dataset.unknownLabel);
      } finally {
        this.chatBusy = false;
        this.refreshChallenge();
        this.syncControls();
      }
    }

    validConsultation(value) {
      return Boolean(value && typeof value === 'object' && !Array.isArray(value)
        && Object.keys(value).every((key) => ['route', 'reply', 'proposal'].includes(key))
        && ['CLARIFY', 'PROPOSE', 'CONFIGURATOR', 'CONTACT'].includes(value.route)
        && typeof value.reply === 'string' && value.reply.length > 0 && value.reply.length <= 4000
        && (value.route === 'PROPOSE' ? this.validProposal(value.proposal) : value.proposal === null));
    }

    releaseUnadmittedConsultation(value, attempt) {
      const keys = ['ok', 'error', 'retryForbidden', 'admission', 'requestId', 'canStartNewAttempt'];
      if (!value || typeof value !== 'object' || Array.isArray(value)
        || Object.keys(value).length !== keys.length || !keys.every((key) => Object.hasOwn(value, key))
        || value.ok !== false || value.error !== 'generation_busy' || value.retryForbidden !== true
        || value.admission !== 'NOT_ADMITTED' || value.canStartNewAttempt !== true
        || value.requestId !== attempt.requestId || attempt.status !== 'SUBMITTED'
        || attempt.turnCount !== this.turnCount || this.turnCount < 1) return false;
      const unadmitted = { ...attempt, status: 'NOT_ADMITTED', turnCount: attempt.turnCount - 1 };
      if (!this.saveAttempt(this.chatAttemptKey, unadmitted)) return false;
      this.chatAttempt = unadmitted;
      this.turnCount = unadmitted.turnCount;
      this.operationLocked = false;
      this.invalidateProposal();
      return true;
    }

    async readJson(response, byteLimit) {
      const declaredLength = response.headers.get('content-length');
      if (declaredLength !== null && /^\d+$/.test(declaredLength) && Number(declaredLength) > byteLimit) {
        await response.body?.cancel();
        throw new Error('response_too_large');
      }
      if (!response.body) throw new Error('missing_response');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let bytes = 0;
      let content = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          bytes += value.byteLength;
          if (bytes > byteLimit) throw new Error('response_too_large');
          content += decoder.decode(value, { stream: true });
        }
        content += decoder.decode();
        return JSON.parse(content);
      } catch (error) {
        await reader.cancel();
        throw error;
      } finally { reader.releaseLock(); }
    }

    validProposal(value) {
      return Boolean(value && typeof value === 'object' && !Array.isArray(value)
        && Object.keys(value).length === 4
        && Object.keys(value).every((key) => ['proposalId', 'revision', 'summary', 'expiresAt'].includes(key))
        && UUID.test(value.proposalId)
        && Number.isSafeInteger(value.revision) && value.revision > 0
        && typeof value.summary === 'string' && value.summary.trim().length > 0 && value.summary.length <= 4000
        && Number.isSafeInteger(value.expiresAt) && value.expiresAt > Date.now());
    }

    async requestPreview(button) {
      if (this.chatBusy || this.generationBusy || this.qaProbeBusy || this.operationLocked || this.previewLocked) return;
      if (!this.qaAccessReady()) { this.showNotice(this.dataset.qaRequiredLabel, 'progress'); return; }
      if (!this.validProposal(this.currentProposal)) { this.invalidateProposal(); this.showNotice(this.dataset.expiredLabel); return; }
      if (button !== this.confirmButton || !this.messages.contains(button)) return;
      if (!this.serviceAvailable()) { this.showNotice(this.dataset.disabledLabel); return; }
      if (!this.turnstileToken) { this.showNotice(this.dataset.challengeLabel); return; }
      const proposal = { ...this.currentProposal };
      const attempt = this.reserveAttempt('PREVIEW');
      if (!attempt) return;
      const token = this.consumeToken();
      this.generationBusy = true;
      this.previewLocked = true;
      this.invalidateProposal();
      this.notice.hidden = true;
      this.showNotice(this.dataset.generatingLabel, 'progress');
      this.syncControls();
      try {
        const response = await fetch(`${this.endpoint}/v2/confirm-preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.sessionId, requestId: attempt.requestId, action: 'CONFIRM_PREVIEW',
            proposalId: proposal.proposalId, revision: proposal.revision, turnstileToken: token }),
          signal: AbortSignal.timeout(235_000),
          credentials: 'omit',
          redirect: 'error',
        });
        const data = await this.readJson(response, 13 * 1024 * 1024);
        if (!response.ok) {
          this.finishAttempt('PREVIEW', 'BLOCKED');
          this.showNotice(this.dataset.lockedErrorLabel);
          return;
        }
        if (!this.safeImageUrl(data.imageUrl)) throw new Error('invalid_preview');
        if (!this.finishAttempt('PREVIEW', 'SUCCEEDED')) return;
        this.notice.hidden = true;
        this.appendPreview(data.imageUrl);
        this.showNotice(this.dataset.completedLabel, 'progress');
      } catch {
        this.finishAttempt('PREVIEW', 'UNKNOWN');
        this.showNotice(this.dataset.unknownLabel);
      } finally {
        this.generationBusy = false;
        this.refreshChallenge();
        this.syncControls();
      }
    }

    safeImageUrl(value) {
      if (typeof value !== 'string' || value.length > 12 * 1024 * 1024) return false;
      if (/^data:image\/(?:jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(value)) return true;
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password;
      } catch { return false; }
    }

    invalidateProposal() {
      clearTimeout(this.expiryTimer);
      this.currentProposal = null;
      this.messages?.querySelectorAll('[data-ai-chat-generate]').forEach((element) => element.remove());
      this.confirmButton = null;
      this.syncControls();
    }

    scheduleExpiry() {
      clearTimeout(this.expiryTimer);
      if (!this.currentProposal) return;
      this.expiryTimer = setTimeout(() => {
        this.invalidateProposal();
        this.showNotice(this.dataset.expiredLabel);
      }, Math.min(this.currentProposal.expiresAt - Date.now(), 2_147_483_647));
    }

    setupChallenge() {
      if (!this.challenge || this.widgetId !== undefined || this.operationLocked || this.previewLocked || !this.serviceAvailable()) return;
      if (!window.turnstile) {
        this.showNotice(this.dataset.challengeLabel);
        this.challengeLoad ||= () => this.setupChallenge();
        window.addEventListener('load', this.challengeLoad, { once: true });
        return;
      }
      this.widgetId = window.turnstile.render(this.challenge, {
        sitekey: this.dataset.turnstileSitekey,
        action: 'crown-preview',
        language: this.locale,
        size: window.matchMedia?.('(max-width: 420px)').matches ? 'compact' : 'normal',
        callback: (token) => {
          this.turnstileToken = !this.chatBusy && !this.generationBusy && !this.operationLocked
            && typeof token === 'string' && token.length > 0 && token !== this.lastConsumedToken ? token : null;
          this.syncControls();
        },
        'expired-callback': () => { this.turnstileToken = null; this.syncControls(); },
        'error-callback': () => { this.turnstileToken = null; this.syncControls(); this.showNotice(this.dataset.challengeLabel); },
      });
    }

    consumeToken() {
      const token = this.turnstileToken;
      this.lastConsumedToken = token;
      this.turnstileToken = null;
      return token;
    }

    refreshChallenge() {
      this.turnstileToken = null;
      if (!this.operationLocked && !this.previewLocked && this.serviceAvailable()) {
        if (this.widgetId !== undefined && window.turnstile) window.turnstile.reset(this.widgetId);
        else this.setupChallenge();
      }
    }

    syncControls() {
      this.syncQaCodeControls();
      const busy = Boolean(this.chatBusy || this.generationBusy || this.qaProbeBusy);
      const blocked = busy || this.operationLocked || !this.serviceAvailable() || !this.qaAccessReady();
      if (this.qaProbeButton) this.qaProbeButton.disabled = !this.qaMode || busy || this.operationLocked
        || !this.storageAvailable || !this.endpoint || !UUID.test(this.sessionId);
      const chatFinished = this.previewLocked || this.turnCount >= MAX_TURNS;
      if (this.turnsCountNode) this.turnsCountNode.textContent = String(Math.max(0, MAX_TURNS - this.turnCount));
      if (this.submitButton) this.submitButton.disabled = blocked || chatFinished || !this.turnstileToken;
      if (this.input) this.input.disabled = busy || chatFinished;
      if (this.delivery) this.delivery.disabled = busy || chatFinished;
      if (this.confirmButton) this.confirmButton.disabled = blocked || this.previewLocked || !this.turnstileToken || !this.validProposal(this.currentProposal);
      this.setAttribute('aria-busy', String(busy));
    }

    appendMessage(role, text, proposal = null, { recovered = false, recoveredLocale = this.locale } = {}) {
      const message = document.createElement('article');
      message.className = `cl-ai-crown-chat__message cl-ai-crown-chat__message--${role}`;
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      message.append(paragraph);
      if (proposal) {
        const heading = document.createElement('h3');
        heading.textContent = this.dataset.summaryLabel;
        const summary = document.createElement('p');
        summary.dataset.aiChatSummary = '';
        summary.textContent = proposal.summary;
        message.append(heading, summary);
        if (this.locale === 'en') {
          const canonicalNote = document.createElement('p');
          canonicalNote.textContent = this.dataset.canonicalLabelsNote;
          message.append(canonicalNote);
        }
        if (!this.previewLocked && !recovered) {
          this.confirmButton = document.createElement('button');
          this.confirmButton.type = 'button';
          this.confirmButton.className = 'button button--secondary';
          this.confirmButton.dataset.aiChatGenerate = '';
          this.confirmButton.textContent = this.dataset.generateLabel;
          message.append(this.confirmButton);
        }
      }
      if (role === 'assistant') {
        if (recovered) {
          const recoveredNote = document.createElement('p');
          recoveredNote.className = 'cl-ai-crown-chat__recovered';
          recoveredNote.textContent = recoveredLocale === this.locale ? this.dataset.recoveredLabel
            : recoveredLocale === 'it' ? this.dataset.recoveredItalianLabel : this.dataset.recoveredEnglishLabel;
          message.append(recoveredNote);
        }
        this.appendCopyAction(message, proposal?.summary || text, Boolean(proposal));
        this.appendConfiguratorLink(message);
      }
      this.messages.append(message);
      this.syncControls();
      this.scrollMessage(message);
    }

    appendConfiguratorLink(message) {
      const note = document.createElement('p');
      note.className = 'cl-ai-crown-chat__handoff';
      note.textContent = this.dataset.handoffLabel;
      const link = document.createElement('a');
      link.className = 'cl-ai-crown-chat__link';
      link.href = this.localizedConfiguratorUrl();
      link.textContent = this.dataset.configuratorLabel;
      message.append(note, link);
    }

    localizedConfiguratorUrl() {
      const configured = this.dataset.configuratorUrl || '/pages/configuratore';
      if (!/^\/(?!\/)/.test(configured)) {
        return this.safeEndpoint(configured) || this.localizedStorePath('/pages/configuratore');
      }
      return this.localizedStorePath(configured);
    }

    localizedStorePath(value) {
      const root = this.dataset.localizedRoot || (this.locale === 'en' ? '/en' : '/');
      const safeRoot = /^\/(?:[A-Za-z0-9-]+\/?)?$/.test(root) ? root.replace(/\/$/, '')
        : this.locale === 'en' ? '/en' : '';
      const path = /^\/(?!\/)/.test(value) && !/[\\\u0000-\u001f\u007f]/.test(value) ? value : '/pages/configuratore';
      // Only the two supported locale prefixes are presentation routing; IDs,
      // session keys, request IDs and provider attempts are deliberately shared.
      return safeRoot + path.replace(/^\/(?:it|en)(?=\/|$)/, '');
    }

    appendCopyAction(message, text, hasProposal) {
      const copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'button button--secondary';
      copy.dataset.aiChatCopy = '';
      copy.textContent = hasProposal ? this.dataset.copyProposalLabel : this.dataset.copyReplyLabel;
      const status = document.createElement('p');
      status.className = 'cl-ai-crown-chat__copy-status';
      status.dataset.aiChatCopyStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      const fallback = document.createElement('textarea');
      fallback.className = 'cl-ai-crown-chat__copy-text';
      fallback.dataset.aiChatCopyText = '';
      fallback.value = text;
      fallback.readOnly = true;
      fallback.hidden = true;
      fallback.rows = 4;
      fallback.setAttribute('aria-label', this.dataset.copyTextLabel);
      message.append(copy, status, fallback);
    }

    async copyCompletedText(button) {
      const message = button.closest('.cl-ai-crown-chat__message--assistant');
      const fallback = message?.querySelector('[data-ai-chat-copy-text]');
      const status = message?.querySelector('[data-ai-chat-copy-status]');
      if (!this.messages?.contains(button) || !fallback || !status || button.disabled
        || typeof fallback.value !== 'string' || !fallback.value.trim() || fallback.value.length > 4000) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = '';
      try {
        if (typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function') throw new Error('clipboard_unavailable');
        await navigator.clipboard.writeText(fallback.value);
        fallback.hidden = true;
        status.textContent = this.dataset.proposalCopiedLabel;
      } catch {
        fallback.hidden = false;
        fallback.focus();
        fallback.select();
        status.textContent = this.dataset.copyManuallyLabel;
      } finally {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }
    }

    recoveryMatchesAttempt(value) {
      return Boolean(this.storageAvailable && this.chatAttempt?.status === 'SUCCEEDED'
        && value.sessionId === this.sessionId && value.requestId === this.chatAttempt.requestId
        && value.turnCount === this.chatAttempt.turnCount
        && sessionStorage.getItem(this.sessionKey) === this.sessionId);
    }

    validRecovery(value, now = Date.now()) {
      const legacy = value?.version === 1;
      const keys = ['version', 'sessionId', 'requestId', 'turnCount', 'savedAt', 'expiresAt', 'reply', 'proposal',
        ...(legacy ? [] : ['locale'])];
      return Boolean(value && typeof value === 'object' && !Array.isArray(value)
        && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key))
        && (legacy || value.version === RECOVERY_VERSION && ['it', 'en'].includes(value.locale))
        && UUID.test(value.sessionId) && UUID.test(value.requestId)
        && Number.isSafeInteger(value.turnCount) && value.turnCount > 0 && value.turnCount <= MAX_TURNS
        && Number.isSafeInteger(value.savedAt) && value.savedAt <= now
        && Number.isSafeInteger(value.expiresAt) && value.expiresAt > now
        && value.expiresAt <= value.savedAt + RECOVERY_TTL
        && typeof value.reply === 'string' && value.reply.trim().length > 0 && value.reply.length <= 4000
        && (value.proposal === null || (this.validProposal(value.proposal) && value.expiresAt <= value.proposal.expiresAt)));
    }

    cacheCompletedReply(value, attempt) {
      if (!this.validConsultation(value) || !attempt || attempt.status !== 'SUCCEEDED') return false;
      try {
        const savedAt = Date.now();
        const cache = { version: RECOVERY_VERSION, locale: this.locale, sessionId: this.sessionId, requestId: attempt.requestId,
          turnCount: attempt.turnCount, savedAt,
          expiresAt: Math.min(savedAt + RECOVERY_TTL, value.proposal?.expiresAt || savedAt + RECOVERY_TTL),
          reply: value.reply, proposal: value.proposal ? { ...value.proposal } : null };
        const encoded = JSON.stringify(cache);
        if (!this.recoveryMatchesAttempt(cache) || !this.validRecovery(cache) || encoded.length > RECOVERY_MAX_LENGTH) return false;
        sessionStorage.setItem(this.recoveryKey, encoded);
        return sessionStorage.getItem(this.recoveryKey) === encoded;
      } catch { return false; }
    }

    restoreCompletedReply() {
      try {
        const encoded = sessionStorage.getItem(this.recoveryKey);
        if (encoded === null) return false;
        if (encoded.length > RECOVERY_MAX_LENGTH) throw new Error('recovery_too_large');
        const cache = JSON.parse(encoded);
        if (!this.validRecovery(cache) || !this.recoveryMatchesAttempt(cache)) throw new Error('recovery_not_current');
        this.appendMessage('assistant', cache.reply, cache.proposal, { recovered: true, recoveredLocale: cache.locale || 'it' });
        return true;
      } catch {
        try { sessionStorage.removeItem(this.recoveryKey); } catch {}
        return false;
      }
    }

    appendPreview(imageUrl) {
      const message = document.createElement('article');
      message.className = 'cl-ai-crown-chat__message cl-ai-crown-chat__message--assistant';
      const image = document.createElement('img');
      image.alt = this.dataset.previewAlt;
      image.width = 1024;
      image.height = 1024;
      image.addEventListener('error', () => {
        image.remove();
        const warning = document.createElement('p');
        warning.textContent = this.dataset.previewFailedLabel;
        message.append(warning);
        this.showNotice(this.dataset.previewFailedLabel);
      }, { once: true });
      image.src = imageUrl;
      const caption = document.createElement('p');
      caption.textContent = this.dataset.previewCaption;
      message.append(image, caption);
      this.appendConfiguratorLink(message);
      this.messages.append(message);
      this.scrollMessage(message);
    }

    scrollMessage(message) {
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      message.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    }

    showNotice(text, state = 'error') {
      if (!this.notice) return;
      this.notice.textContent = text;
      this.notice.dataset.state = state;
      this.notice.hidden = false;
    }

    adoptQaInvitation() {
      if (!this.qaAutomatic) return;
      try {
        const fragment = window.location.hash;
        if (!fragment.startsWith('#qa-session=')) return;
        const invitedSessionId = fragment.slice('#qa-session='.length);
        if (!UUID_V4.test(invitedSessionId)) throw new Error('invalid_qa_invitation');
        const existingSessionId = sessionStorage.getItem(this.sessionKey);
        if (existingSessionId !== null && !UUID.test(existingSessionId)) throw new Error('invalid_session');
        if (existingSessionId !== invitedSessionId) {
          if (sessionStorage.getItem(this.chatAttemptKey) !== null
            || sessionStorage.getItem(this.previewAttemptKey) !== null) throw new Error('qa_invitation_attempt_present');
          sessionStorage.setItem(this.sessionKey, invitedSessionId);
          if (sessionStorage.getItem(this.sessionKey) !== invitedSessionId) throw new Error('session_not_saved');
        }
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
      } catch {
        this.storageAvailable = false;
      }
    }

    readOrCreateSessionId() {
      try {
        const existing = sessionStorage.getItem(this.sessionKey);
        if (existing !== null) {
          if (!UUID.test(existing)) throw new Error('invalid_session');
          return existing;
        }
        const value = crypto.randomUUID();
        if (!UUID.test(value)) throw new Error('invalid_session');
        sessionStorage.setItem(this.sessionKey, value);
        if (sessionStorage.getItem(this.sessionKey) !== value) throw new Error('session_not_saved');
        return value;
      } catch { this.storageAvailable = false; return ''; }
    }

    readAttempt(key, kind) {
      try {
        const value = sessionStorage.getItem(key);
        if (value === null) return null;
        const attempt = JSON.parse(value);
        if (!attempt || Object.keys(attempt).length !== 5 || attempt.sessionId !== this.sessionId || !UUID.test(attempt.requestId)
          || attempt.kind !== kind || !['SUBMITTED', 'SUCCEEDED', 'BLOCKED', 'UNKNOWN', ...(kind === 'CONSULTATION' ? ['NOT_ADMITTED'] : [])].includes(attempt.status)
          || !Number.isSafeInteger(attempt.turnCount) || attempt.turnCount < 0 || attempt.turnCount > MAX_TURNS) throw new Error('invalid_attempt');
        return attempt;
      } catch { this.storageAvailable = false; return null; }
    }

    saveAttempt(key, attempt) {
      try {
        const value = JSON.stringify(attempt);
        sessionStorage.setItem(key, value);
        if (sessionStorage.getItem(key) !== value) throw new Error('attempt_not_saved');
        return true;
      } catch {
        this.storageAvailable = false;
        this.operationLocked = true;
        this.invalidateProposal();
        this.showNotice(this.dataset.storageErrorLabel);
        return false;
      }
    }

    reserveAttempt(kind) {
      if (!this.storageAvailable || this.operationLocked) return null;
      try {
        const requestId = crypto.randomUUID();
        if (!UUID.test(requestId)) throw new Error('invalid_request');
        const attempt = { sessionId: this.sessionId, requestId, kind, status: 'SUBMITTED',
          turnCount: this.turnCount + (kind === 'CONSULTATION' ? 1 : 0) };
        const key = kind === 'CONSULTATION' ? this.chatAttemptKey : this.previewAttemptKey;
        const current = this.readAttempt(key, kind);
        const otherKind = kind === 'CONSULTATION' ? 'PREVIEW' : 'CONSULTATION';
        const otherKey = kind === 'CONSULTATION' ? this.previewAttemptKey : this.chatAttemptKey;
        const other = this.readAttempt(otherKey, otherKind);
        if (!this.storageAvailable || (current && (kind === 'PREVIEW' || !['SUCCEEDED', 'NOT_ADMITTED'].includes(current.status)))
          || (other && other.status !== 'SUCCEEDED')
          || (kind === 'CONSULTATION' && current && current.turnCount !== this.turnCount)) {
          this.operationLocked = true;
          this.invalidateProposal();
          this.showNotice(this.dataset.unknownLabel);
          return null;
        }
        if (!this.saveAttempt(key, attempt)) return null;
        if (kind === 'CONSULTATION') { this.chatAttempt = attempt; this.turnCount = attempt.turnCount; }
        else this.previewAttempt = attempt;
        return attempt;
      } catch {
        this.storageAvailable = false;
        this.operationLocked = true;
        this.invalidateProposal();
        this.showNotice(this.dataset.storageErrorLabel);
        return null;
      }
    }

    finishAttempt(kind, status) {
      const attempt = kind === 'CONSULTATION' ? this.chatAttempt : this.previewAttempt;
      const key = kind === 'CONSULTATION' ? this.chatAttemptKey : this.previewAttemptKey;
      attempt.status = status;
      this.operationLocked = status !== 'SUCCEEDED';
      if (!this.saveAttempt(key, attempt)) return false;
      return true;
    }
  }

  customElements.define('cl-ai-crown-chat', CrownChat);
})();
